import React from 'react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    Text,
    Box,
    Flex,
    Progress,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { MdAccessTime, MdLockOutline } from 'react-icons/md'

import { useInactivityContext } from '~config/inactivity-timeout-context'
import { useUserContext } from '~config/user-context'
import ReauthForm from '~components/auth/reauth-form'

export default function InactivityModal() {
    const {
        isOpen,
        countdown,
        countdownStartMillis,
        isExpired,
        lockedEmail,
        resetInactivityTimer,
    } = useInactivityContext()
    const { signOut } = useUserContext()

    const fadeIn = keyframes`
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    `

    return (
        <Modal
            isOpen={isOpen}
            onClose={isExpired ? () => {} : resetInactivityTimer}
            closeOnOverlayClick={!isExpired}
            closeOnEsc={!isExpired}
            isCentered
            size="md"
        >
            <ModalOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(5px)" />
            <ModalContent
                bg="white"
                borderRadius="2xl"
                boxShadow="xl"
                p={6}
                m={4}
                animation={`${fadeIn} 0.3s ease-out`}
            >
                <ModalBody>
                    <Flex direction="column" align="center">
                        {isExpired ? (
                            <>
                                <Box
                                    bg="blue.50"
                                    p={4}
                                    borderRadius="full"
                                    mb={6}
                                >
                                    <MdLockOutline size="40" color="#3182CE" />
                                </Box>
                                <Text
                                    fontSize="2xl"
                                    fontWeight="bold"
                                    color="gray.800"
                                    mb={2}
                                    textAlign="center"
                                >
                                    Session locked
                                </Text>
                                <Text
                                    fontSize="md"
                                    color="gray.600"
                                    textAlign="center"
                                    mb={6}
                                >
                                    We locked your screen after a period of
                                    inactivity. Enter your password to unlock and
                                    pick up right where you left off.
                                </Text>
                                <ReauthForm
                                    email={lockedEmail}
                                    onReauthenticated={resetInactivityTimer}
                                    onUseDifferentAccount={() => signOut()}
                                />
                            </>
                        ) : (
                            <>
                                <Box
                                    bg="blue.50"
                                    p={4}
                                    borderRadius="full"
                                    mb={6}
                                >
                                    <MdAccessTime size="40" color="#3182CE" />
                                </Box>
                                <Text
                                    fontSize="2xl"
                                    fontWeight="bold"
                                    color="gray.800"
                                    mb={4}
                                    textAlign="center"
                                >
                                    Locking Soon
                                </Text>
                                <Text
                                    fontSize="md"
                                    color="gray.600"
                                    textAlign="center"
                                    mb={6}
                                >
                                    Your screen will lock in{' '}
                                    <Text
                                        as="span"
                                        fontWeight="semibold"
                                        color="blue.500"
                                    >
                                        {countdown} seconds
                                    </Text>{' '}
                                    due to inactivity.
                                </Text>
                                <Progress
                                    value={
                                        (countdown /
                                            (countdownStartMillis / 1000)) *
                                        100
                                    }
                                    isAnimated
                                    transition="0.1s"
                                    size="sm"
                                    colorScheme="blue"
                                    width="100%"
                                    borderRadius="full"
                                    mb={6}
                                />
                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                    textAlign="center"
                                >
                                    Move your cursor or press any key to remain
                                    active.
                                </Text>
                            </>
                        )}
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}
